import React from "react";
import { ListResponse } from "src/types";

import { fetchAllWorkspaces } from "@admin/api/workspace";
import { showErrorToast } from "@shared/toasts/Toasts";

interface UseGetAllWorkspacesProps {
    pageSize?: number;
    pageIndex?: number;
    sortCriteria?: string;
    sortDirection?: string;
}

export const useGetAllWorkspaces = ({
    pageSize = 50,
    pageIndex = 1,
    sortCriteria,
    sortDirection,
}: UseGetAllWorkspacesProps = {}) => {
    const [data, setData] = React.useState<ListResponse<API.Workspace> | null>(null);
    const [isLoading, setIsLoading] = React.useState(false);

    const refetch = React.useCallback(() => {
        setIsLoading(true);

        void fetchAllWorkspaces({
            pageSize,
            pageIndex,
            sortCriteria,
            sortDirection,
        })
            .then((response) => setData(response))
            .catch((error) => {
                const err = error as Error;
                showErrorToast(err.message);
            })
            .finally(() => setIsLoading(false));
    }, [pageSize, pageIndex, sortCriteria, sortDirection]);

    React.useLayoutEffect(() => {
        refetch();
    }, [refetch]);

    const removeWorkspace = React.useCallback((workspaceId: number) => {
        setData((prevData) => {
            if (!prevData) {
                return prevData;
            }
            return {
                ...prevData,
                list: prevData.list.filter((workspace) => workspace.id !== workspaceId),
                count: prevData.count - 1,
            };
        });
    }, []);

    const updateWorkspace = React.useCallback((updatedWorkspace: API.Workspace) => {
        setData((prevData) => {
            if (!prevData) {
                return prevData;
            }
            return {
                ...prevData,
                list: prevData.list.map((workspace) => (workspace.id === updatedWorkspace.id ? updatedWorkspace : workspace)),
            };
        });
    }, []);

    return { data, isLoading, refetch, removeWorkspace, updateWorkspace };
};
